<script lang="ts" setup>
import { computed, onMounted, PropType, ref, toRef, useSlots, watch } from "vue";
import PaginationControl from "./PaginationControl.vue";
import { PermissionAction } from "@shared/components/core/helpers/securityTypes";
import { useI18n } from "vue-i18n";
import VaIconMagnifier from "@shared/components/icons/VaIconMagnifier.vue";

const { t } = useI18n();
const sortBy = ref("");
const sortingOrder = ref<"asc" | "desc">("asc");
const localItems = ref<any[]>([]);

const props = defineProps({
  columns: {
    type: Array as PropType<any[]>,
    required: true,
  },
  defaultSortBy: {
    type: String,
    default: "",
  },
  items: {
    type: [Array, Function] as PropType<any[] | ((query: string) => Promise<any[]>)>,
    required: true,
  },
  title: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object as PropType<{ page: number; perPage: number; total: number }>,
    required: false,
  },
  editLabel: {
    type: String,
    default: "",
  },
  deleteLabel: {
    type: String,
    default: "",
  },
  showEditButton: {
    type: [Boolean, Function] as PropType<
      boolean | ((rowData: any) => boolean)
    >,
    default: true,
  },
  showDeleteButton: {
    type: [Boolean, Function] as PropType<
      boolean | ((rowData: any) => boolean)
    >,
    default: true,
  },
  basePermission: {
    type: String,
    default: "",
  },
  secondaryActions: {
    type: Array as PropType<
      Array<{
        label: string;
        icon: string;
        action: (rowData: any) => void;
        requiredPermission?: string;
        requiredAction?: PermissionAction;
        visible?: (rowData: any) => boolean;
      }>
    >,
    default: () => [],
    validator: (actions: any[]) =>
      actions.every(
        (a) => typeof a.label === "string" && typeof a.action === "function"
      ),
  },
});

const getValueByPath = (obj: any, path: string): any => {
  return path.split('.').reduce((o, p) => (o ? o[p] : "ZZZZZZ"), obj);
};

const sortedItems = computed(() => {
  if (!sortBy.value) return localItems.value;
  if (!localItems.value || localItems.value.length === 0) return [];
  let sortKey = sortBy.value;

  // get the key from columns based on the received sortBy value if there is a column with name property matching the sortBy value
  const column = props.columns.find(col => col.name === sortBy.value);
  if (column && column.key) {
    sortKey = column.key;
  }

  return [...localItems.value].sort((a, b) => {
    const aValue =  getValueByPath(a, sortKey);
    const bValue =  getValueByPath(b, sortKey);

    if (aValue < bValue)
      return sortingOrder.value === "asc" ? -1 : 1;
    if (aValue > bValue)
      return sortingOrder.value === "asc" ? 1 : -1;
    return 0;
  });
});

const functionMode = computed(() => {
  if (typeof props.items === "function") {
    return props.items;
  }
  return null;
});

const localLoading = toRef(props, "loading");
const localPagination = ref({ page: 1, perPage: 10, total: 0 })

const getDataFromFunction = async (query: string) => {
  if (functionMode.value) {
    const data = await functionMode.value(query);
    localItems.value = data;
    if (localPagination.value) {
      localPagination.value.total = data.length;
      localPagination.value.page = 1;
    }
  }
};

const refresh = async () => {
  if (functionMode.value) {
    await getDataFromFunction(searchText.value);
  }
};

onMounted(() => {
  if (functionMode.value) {
    getDataFromFunction("");
  } else if (Array.isArray(props.items)) {
    localItems.value = props.items;
  }

  if (props.defaultSortBy) {
    const splittedValue = props.defaultSortBy.split(" ");
    if (splittedValue.length === 2) {
      sortBy.value = splittedValue[0];
      sortingOrder.value =
        splittedValue[1].toLowerCase() === "desc" ? "desc" : "asc";
    } else {
      sortBy.value = props.defaultSortBy;
    }
  }
});

const shouldShowEditButton = (rowData: any): boolean => {
  if (typeof props.showEditButton === "function") {
    return props.showEditButton(rowData);
  }
  return Boolean(props.showEditButton);
};

const shouldShowDeleteButton = (rowData: any): boolean => {
  if (typeof props.showDeleteButton === "function") {
    return props.showDeleteButton(rowData);
  }
  return Boolean(props.showDeleteButton);
};

const showSecondaryActions = (rowData: any): boolean => {
  return props.secondaryActions.some((action) =>
    action.visible ? action.visible(rowData) : true
  );
};

const slots = useSlots();
const searchText = ref("");
const debounceId: { current: number | null } = { current: null };
const emit = defineEmits<{
  (event: "edit-row", row: any): void;
  (event: "delete-row", profile: any): void;
}>();

watch(
  () => props.pagination,
  (newPagination) => {
    if (newPagination) {
      localPagination.value = { ...newPagination };
    }
  },
  { immediate: true, deep: true }
);

watch(searchText, (newValue) => {
  localPagination.value.page = 1;
  if (functionMode.value) {
    if (debounceId.current) {
      clearTimeout(debounceId.current);
    }
    debounceId.current = window.setTimeout(() => {
      getDataFromFunction(newValue);
      debounceId.current = null;
    }, 300);
  }
});

watch(
  () => props.items,
  (newItems) => {
    if (!functionMode.value && Array.isArray(newItems)) {
      localItems.value = newItems;
    } else if (functionMode.value) {
      functionMode.value("").then((data) => {
        localItems.value = data;
        localPagination.value.total = data.length;
        localPagination.value.page = 1;
      });
    }
  }
);

defineExpose({
  refresh,
})
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-row justify-between items-center mb-4">
      <h6 v-if="props.title" class="va-h6">{{ props.title }}</h6>
      <slot name="appendTitle" />
      <div class="flex-1 flex justify-end">
        <VaInput
          v-model="searchText"
          :placeholder="t('general.search')"
          class="w-56"
          clearable
        >
          <template #prependInner>
            <VaIconMagnifier />
          </template>
        </VaInput>
      </div>
    </div>
    <VaDataTable
      v-model:sort-by="sortBy"
      v-model:sorting-order="sortingOrder"
      :columns="props.columns"
      :items="sortedItems"
      :filter="functionMode ? undefined : searchText"
      :loading="localLoading"
      :per-page="localPagination.perPage"
      :current-page="localPagination.page"
      :default-sort-by="props.defaultSortBy"
      class="va-data-table"
    >
      <template #cell(actions)="{ rowData }">
        <div class="flex gap-2 justify-end">
          <VaButton
            v-if="shouldShowEditButton(rowData)"
            v-permission="[props.basePermission, PermissionAction.Write]"
            v-tooltip="props.editLabel"
            preset="primary"
            size="small"
            icon="mso-edit"
            @click="emit('edit-row', rowData as any)"
          />
          <VaButton
            v-if="shouldShowDeleteButton(rowData)"
            v-permission="[props.basePermission, PermissionAction.Delete]"
            v-tooltip="props.deleteLabel"
            preset="primary"
            size="small"
            icon="mso-delete"
            color="danger"
            @click="emit('delete-row', rowData as any)"
          />
          <slot name="actions-append" :row="rowData" />
          <VaMenu v-if="showSecondaryActions(rowData)">
            <template #anchor>
              <VaButton color="background-element" size="small">...</VaButton>
            </template>
            <VaValue
              v-for="(action, index) in props.secondaryActions"
              :key="index"
            >
              <VaMenuItem
                v-if="action.visible ? action.visible(rowData) : true"
                v-permission="[
                  action.requiredPermission,
                  action.requiredAction,
                ]"
                :icon="action.icon"
                @selected="() => action.action(rowData)"
              >
                {{ t(action.label) }}
              </VaMenuItem>
            </VaValue>
          </VaMenu>

        </div>
      </template>
      <template v-for="col in Object.keys(slots)" :key="col" #[col]="slotProps">
        <slot v-if="slots[col]" :name="col" v-bind="slotProps" />
      </template>
    </VaDataTable>

    <PaginationControl
      v-model:page="localPagination.page"
      v-model:per-page="localPagination.perPage"
      :total="localPagination.total"
      :loading="$props.loading"
    />

    <VaAlert v-if="functionMode" type="info" class="mt-4 w-full" dense closeable border-color="info" border="left">
      {{ t("general.warnings.maxDisplayedRecords") }}
    </VaAlert>
  </div>
</template>

<style lang="scss" scoped>
.va-data-table {
  ::v-deep(.va-data-table__table-tr) {
    border-bottom: 1px solid var(--va-background-border);
  }
}
</style>
